import axios from 'axios';
import { logger } from '../utils/logger';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

logger.debug('API Configuration:', {
  API_BASE_URL,
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  MODE: import.meta.env.MODE,
  DEV: import.meta.env.DEV
});

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  logger.api(config.method?.toUpperCase() || 'UNKNOWN', config.url || '', {
    hasData: !!config.data,
    timeout: config.timeout
  });

  return config;
});

api.interceptors.response.use(
  (response) => {
    logger.api('SUCCESS', response.config.url || '', {
      status: response.status
    });
    return response;
  },
  (error) => {
    const errorInfo = {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      code: error.code
    };

    if (error.code === 'ECONNABORTED') {
      logger.error('Request timeout - Backend nicht rechtzeitig erreicht!');
    } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      logger.error('Network connection error - Backend nicht erreichbar!');
    } else {
      logger.error('API Error:', errorInfo);
    }

    if (error.response?.status === 401) {
      if (!error.config?.url?.includes('/auth/login')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials: { email: string; password: string }) => {
    logger.auth('Login attempt', { email: credentials.email });
    return api.post('/auth/login', credentials);
  },
  verify2FA: (data: { userId: number; token: string }) => {
    logger.auth('2FA verification attempt', { userId: data.userId });
    return api.post('/auth/verify-2fa', data);
  },
  validateToken: () => api.get('/auth/validate-token'),
  register: (userData: any) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  setup2FA: () => api.post('/auth/setup-2fa'),
  enable2FA: (data: { token: string; secret: string; backupCodes: string[] }) => 
    api.post('/auth/enable-2fa', data),
  disable2FA: (token: string) => api.post('/auth/disable-2fa', { token }),
  regenerateBackupCodes: (token: string) => api.post('/auth/regenerate-backup-codes', { token }),
  getBackupCodeStatus: () => api.get('/auth/backup-code-status'),
};

export const userAPI = {
  getProfile: () => api.get('/users/me'), // Holt das Benutzerprofil
  updateProfile: (id: number, data: any) => {
    logger.api('PUT', `/users/${id}`, { data });
    return api.put(`/users/${id}`, data);
  },
  changePassword: (id: number, data: { currentPassword: string; password: string }) =>
    api.put(`/users/${id}/password`, data),
  getVacationSummary: (userId?: number) => {
    const endpoint = userId ? `/users/${userId}/vacation-summary` : '/users/me/vacation-summary';
    return api.get(endpoint);
  },

  clockIn: (data: { description?: string }) => {
    // Set header to indicate this is a website clock-in
    return api.post('/worklogs/clock-in', data, {
      headers: {
        'X-Log-Method': 'WEBSITE'
      }
    });
  },

  clockOut: (workLogId: number, data: { description?: string }) => {
    // Set header to indicate this is a website clock-out
    return api.put(`/worklogs/clock-out/${workLogId}`, data, {
      headers: {
        'X-Log-Method': 'WEBSITE'
      }
    });
  },

  getCurrentWorkStatus: () => api.get('/worklogs/current-status'),

  getTodayWorkStatus: () => api.get('/worklogs/today'),
};

export const timeOffAPI = {
  createVacationRequest: (data: {
    startDate: string;
    endDate: string;
    reason?: string;
    substituteUserId?: number;
  }) => api.post('/timeoff/vacation', data),
  deleteTimeOffRequest: (requestId: number) => 
    api.delete(`/timeoff/${requestId}`),
  getTimeOffRequests: (params?: {
    status?: string;
    type?: string;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.type) searchParams.append('type', params.type);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    
    return api.get(`/timeoff?${searchParams}`);
  },

  getAllTimeOffRequestsAdmin: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
    targetUserId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.targetUserId) queryParams.append('targetUserId', params.targetUserId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    return api.get(`/timeoff/admin/all?${queryParams.toString()}`);
  },

  getVacationSummary: (params?: {
    year?: number;
    targetUserId?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.year) searchParams.append('year', params.year.toString());
    if (params?.targetUserId) searchParams.append('targetUserId', params.targetUserId.toString());
    
    return api.get(`/timeoff/vacation-summary?${searchParams}`);
  },

  getAvailableSubstitutes: (params: {
    startDate: string;
    endDate: string;
  }) => {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', params.startDate);
    searchParams.append('endDate', params.endDate);
    
    return api.get(`/timeoff/substitutes?${searchParams}`);
  },

  getPendingRequests: (params?: {
    page?: number;
    limit?: number;
    type?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.type) searchParams.append('type', params.type);
    
    return api.get(`/timeoff/pending?${searchParams}`);
  },

  updateTimeOffStatus: (requestId: number, data: {
    status: 'APPROVED' | 'REJECTED';
    adminNotes?: string;
  }) => api.put(`/timeoff/${requestId}/status`, data),

  createSickLeave: (data: {
    userId: number;
    startDate: string;
    endDate: string;
    reason?: string;
    doctorNote?: boolean;
  }) => api.post('/timeoff/sick', data),

  createVacationRetroactive: (data: {
    userId: number;
    startDate: string;
    endDate: string;
    reason?: string;
    substituteUserId?: number;
    type?: 'VACATION' | 'SPECIAL_LEAVE' | 'SICK'; 
  }) => api.post('/timeoff/vacation/retroactive', data),

  getSickDaysSummary: (params?: {
    year?: number;
    targetUserId?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.year) searchParams.append('year', params.year.toString());
    if (params?.targetUserId) searchParams.append('targetUserId', params.targetUserId.toString());
    
    return api.get(`/timeoff/sick-summary?${searchParams}`);
  },

  getBlockedDates: (params: {
    userId?: number;
    startDate: string;
    endDate: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params.userId) queryParams.append('userId', params.userId.toString());
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    
    return api.get(`/timeoff/blocked-dates?${queryParams.toString()}`);
  },

  getHolidaysInRange: (params: {
    startDate: string;
    endDate: string;
    year?: number;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    if (params.year) queryParams.append('year', params.year.toString());
    
    return api.get(`/timeoff/holidays-in-range?${queryParams.toString()}`);
  },
};

export const adminAPI = {
  getAllUsers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.role) searchParams.append('role', params.role);
    
    return api.get(`/admin/users?${searchParams}`);
  },

  toggleUserStatus: (userId: number) =>
    api.put(`/admin/users/${userId}/toggle-status`),

  reset2FA: (userId: number) =>
    api.put(`/admin/users/${userId}/reset-2fa`),

  updateUserData: (userId: number, data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    vacationDays?: number;
    weeklyHours?: number;
    workDays?: string;
    maxWorkHours?: number;
    startDate?: string;
    isActive?: boolean;
    initialOvertimeMinutes?: number;
  }) => api.put(`/admin/users/${userId}`, data),

  deleteUser: (userId: number) =>
    api.delete(`/admin/users/${userId}`),

  resetUserPassword: (userId: number, newPassword: string) =>
    api.put(`/admin/users/${userId}/reset-password`, { newPassword }),
  getHolidays: (year: number) => 
    api.get(`/admin/holidays?year=${year}`),
  
  createHoliday: (holidayData: { 
    name: string; 
    startDate: string; 
    endDate?: string | null; 
    description?: string; 
    isRecurring?: boolean 
  }) =>
    api.post('/admin/holidays', holidayData),
  
  updateHoliday: (id: number, holidayData: { 
    name?: string; 
    startDate?: string; 
    endDate?: string | null; 
    description?: string; 
    isActive?: boolean; 
    isRecurring?: boolean 
  }) =>
    api.put(`/admin/holidays/${id}`, holidayData),
  
  deleteHoliday: (id: number) =>
    api.delete(`/admin/holidays/${id}`),

  getActiveWorkingUsers: () => 
    api.get('/admin/dashboard/active-users'),
  
  getUsersOffToday: () => 
    api.get('/admin/dashboard/users-off-today'),

  getDashboardStats: () => api.get('/admin/dashboard-stats'),
};

export const timeTrackingAPI = {
  getCurrentStatus: () => api.get('/worklogs/current-status'),
  clockIn: (data: { description?: string }) => api.post('/worklogs/clock-in', data),
  clockOut: (workLogId: number, data: { description?: string }) => 
    api.put(`/worklogs/clock-out/${workLogId}`, data),

  getTodayWorkLogs: () => api.get('/worklogs/today'),

  getUserWorkLogs: (params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    
    return api.get(`/worklogs?${searchParams}`);
  }
};

export const breakRulesAPI = {
  getBreakRules: () => api.get('/admin/break-rules'),
  createBreakRule: (data: {
    minWorkHours: number;
    breakMinutes: number;
    description?: string | null;
    sortOrder?: number;
  }) => api.post('/admin/break-rules', data),
  
  updateBreakRule: (id: number, data: {
    minWorkHours?: number;
    breakMinutes?: number;
    description?: string | null;
    isActive?: boolean;
    sortOrder?: number;
  }) => api.put(`/admin/break-rules/${id}`, data),

  deleteBreakRule: (id: number) => api.delete(`/admin/break-rules/${id}`)
};

export const workLogAdminAPI = {
  getWorkLogs: (params?: {
    userId?: number;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.userId) searchParams.append('userId', params.userId.toString());
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    
    return api.get(`/worklogs/admin?${searchParams}`);
  },
  
  createWorkLog: (data: {
    userId: number;
    startTime: string;
    endTime?: string;
    description?: string;
    applyBreakRules?: boolean;
  }) => api.post('/worklogs/admin', data),

  updateWorkLog: (id: number, data: {
    startTime: string;
    endTime?: string | null;
    description?: string;
    applyBreakRules?: boolean;
  }) => api.put(`/worklogs/admin/${id}`, data),

  deleteWorkLog: (id: number) => api.delete(`/worklogs/admin/${id}`)
};

export const monthlyOverviewAPI = {
  getMonthlyOverview: (params: { year?: number; month?: number; week?: number; startDate?: string; endDate?: string }) =>
    api.get('/monthly/overview', { params }),
  
  getAdminMonthlyOverview: (params: { 
    targetUserId?: number; 
    year?: number; 
    month?: number; 
    week?: number; 
    startDate?: string; 
    endDate?: string 
  }) =>
    api.get('/monthly/admin-overview', { params }),
  
  getAvailablePeriods: () =>
    api.get('/monthly/available-periods')
};


export default api;